import React, { useState, useEffect } from "react";
import { BASE_URL } from "utils/constant";
import CreateDesignModal from "./CreateDesignModal";
import RequireAccess from "components/RequireAccess";
import {
  Download,
  Trash2,
  Search,
  Plus,
  Eye,
  FileImage,
} from "lucide-react";
import DrawingDetailView from "./DesignDetail";
import {
  getStatusBadgeClass,
  downloadDiagram,
  getDesignsByOrg,
  deleteDesignById
} from "../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";

export default function DesignManagement({ currentMember }) {
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  const [designsError, setDesignsError] = useState(null);
  const hasDesignAccess =
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true ||
    currentMember?.design_management === true ||
    currentMember?.design_admin === true;

  const fetchDesigns = async () => {
    try {
      setLoadingDesigns(true);
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      const orgId = org?.id;
      if (!orgId) {
        setDrawings([]);
        setLoadingDesigns(false);
        return;
      }

      const mapped = await getDesignsByOrg(orgId);
      setDrawings(mapped);
    } catch (err) {
      console.error("Failed to load designs", err);
      setDesignsError(err);
      setDrawings([]);
    } finally {
      setLoadingDesigns(false);
    }
  };

  useEffect(() => {
    if (!hasDesignAccess) return;
    fetchDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDesignAccess]);

  if (!hasDesignAccess) {
    return (
      <RequireAccess
        module="Design"
        currentMember={currentMember}
        permission="design_management"
      />
    );
  }

  const handleBack = () => setSelectedDrawing(null);

  const handleSelectDrawing = (drawing) => setSelectedDrawing(drawing);

  // Supports either a full drawing object OR a functional updater (prev => next).
  // This prevents async updates (pins/comments/versions) from clobbering each other.
  const handleUpdateDrawing = (update) => {
    setSelectedDrawing((prevSelected) => {
      const nextSelected = typeof update === "function" ? update(prevSelected) : update;
      const targetId = nextSelected?.id ?? prevSelected?.id;

      if (targetId) {
        setDrawings((prev) =>
          prev.map((d) => (d.id === targetId ? { ...d, ...(nextSelected || {}) } : d))
        );
      }

      return nextSelected;
    });
  };

  const handleCreateDesign = (design) => {
    // This function is now only used for optimistic updates if needed, 
    // but we will rely on refetching for accuracy.
    // However, to keep the prop signature for DesignDashboard, we keep it.
    // But DesignDashboard will now call onRefresh instead of just onCreateDesign
  };

  const handleDeleteDesign = (id) => setDrawings(prev => prev.filter(d => d.id !== id));

  if (selectedDrawing) {
    return (
      <DrawingDetailView
        drawing={selectedDrawing}
        onBack={handleBack}
        onUpdate={handleUpdateDrawing}
        onDelete={handleDeleteDesign}
        currentMember={currentMember}
      />
    );
  }

  return (
    <DesignDashboard
      drawings={drawings}
      onSelectDrawing={handleSelectDrawing}
      onCreateDesign={handleCreateDesign}
      onDeleteDesign={handleDeleteDesign}
      onRefresh={fetchDesigns}
    />
  );
}

function DesignDashboard({ drawings, onSelectDrawing, onCreateDesign, onDeleteDesign, onRefresh }) {
  const { addAlert } = useAlert();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    projectName: "",
    assignee: "",
    fileType: "",
  });

  const projects = ["Skyline Residency", "Green Valley Apartments", "Tech Park Tower", "Sunrise Villas"];
  const teamMembers = ["Rajesh Kumar", "Amit Singh", "Priya Sharma", "Vikram Patel", "Neha Gupta", "Meera Reddy"];
  const designTypes = ["Layout", "Electrical", "HVAC", "Plumbing", "Elevation", "Structural"];

  const assignees = [...new Set(drawings.map(d => d.assignee))];

  const filteredDrawings = drawings.filter(drawing => {
    const matchesSearch =
      drawing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drawing.projectName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = projectFilter === "all" || drawing.projectName === projectFilter;
    const matchesStatus = statusFilter === "all" || drawing.status === statusFilter;
    const matchesType = typeFilter === "all" || drawing.fileType === typeFilter;
    const matchesAssignee = assigneeFilter === "all" || drawing.assignee === assigneeFilter;
    return matchesSearch && matchesProject && matchesStatus && matchesType && matchesAssignee;
  });

  const stats = {
    total: drawings.length,
    approved: drawings.filter(d => d.status === "Approved").length,
    pending: drawings.filter(d => d.status === "Pending" || d.status === "Under Review").length,
    locked: drawings.filter(d => d.status === "Locked").length,
  };

  const handleSubmitCreate = async (createdDesignResponse, originalFormData = null, selectedSite = null) => {
    // If called with createdDesign from the server, use that to add to UI
    if (createdDesignResponse) {
      // Instead of manually constructing the object and guessing the ID,
      // we simply refresh the list from the server.
      // This ensures we get the correct ID and data format.
      if (onRefresh) {
        await onRefresh();
      }

      setShowCreateDialog(false);
      return;
    }

    // Fallback: local creation if no server response provided
    if (!formData.name || !formData.projectName || !formData.assignee || !formData.fileType) {

      return;
    }

    onCreateDesign({
      name: formData.name,
      projectName: formData.projectName,
      assignee: formData.assignee,
      fileType: formData.fileType,
      status: "Pending",
      lastUpdated: new Date().toISOString().split("T")[0],
      approverName: "Pending",
      uploadedBy: formData.assignee,
      linkedTask: undefined,
      linkedTasks: [],
    });

    setFormData({ name: "", projectName: "", assignee: "", fileType: "" });
    setShowCreateDialog(false);
    addAlert("Design created successfully", "success");
  };

  const handleConfirmDelete = () => {
    (async () => {
      if (!deleteId) return;
      try {
        await deleteDesignById(deleteId);
        onDeleteDesign(deleteId);
        addAlert("Design deleted successfully", "success");
      } catch (err) {
        console.error("Delete design failed", err);
        addAlert(err.message || "Failed to delete design", "error");
      } finally {
        setDeleteId(null);
      }
    })();
  };

  return (
    <div className="flex-1 w-full min-h-screen bg-gray-50">
      <div className="w-full px-10 py-8 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Design Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage all project drawings and design files
            </p>
          </div>
          <button
            className="px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg flex items-center gap-2 shadow-sm"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="w-4 h-4" /> Create Design
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Total Designs", value: stats.total, icon: FileImage, bgColor: "bg-sm-secondary/10", iconColor: "text-sm-secondary" },
            { label: "Approved", value: stats.approved, icon: FileImage, bgColor: "bg-success/10", iconColor: "text-success" },
            { label: "Pending", value: stats.pending, icon: FileImage, bgColor: "bg-sm-accent/10", iconColor: "text-sm-accent" },
            { label: "Locked", value: stats.locked, icon: FileImage, bgColor: "bg-sm-primary/10", iconColor: "text-sm-primary" },
          ].map((stat) => (
            <div key={stat.label} className="p-4 border rounded-xl bg-white shadow-sm">
              <div className="flex items-center gap-3">
                <div
                  className={`h-10 w-10 rounded-lg ${stat.bgColor} flex items-center justify-center border`}
                >
                  <stat.icon className={`h-5 w-5 ${stat.iconColor}`} />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              placeholder="Search designs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm border rounded w-full bg-white"
            />
          </div>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="h-9 border rounded bg-white px-2 text-sm"
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-9 border rounded bg-white px-2 text-sm"
          >
            <option value="all">All Types</option>
            {designTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 border rounded bg-white px-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Under Review">Under Review</option>
            <option value="Approved">Approved</option>
            <option value="Locked">Locked</option>
          </select>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="h-9 border rounded bg-white px-2 text-sm"
          >
            <option value="all">All Assignees</option>
            {assignees.map((assignee) => (
              <option key={assignee} value={assignee}>
                {assignee}
              </option>
            ))}
          </select>
        </div>

        <div className="border rounded-xl overflow-hidden flex-1 bg-white shadow-sm">
          <table className="w-full bg-white">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-600">
                <th className="py-2 px-4 font-medium">Design Name</th>
                <th className="py-2 px-4 font-medium">Project</th>
                <th className="py-2 px-4 font-medium">Type</th>
                <th className="py-2 px-4 font-medium">Assignee</th>
                <th className="py-2 px-4 font-medium">Status</th>
                <th className="py-2 px-4 font-medium">Last Updated</th>
                <th className="py-2 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrawings.map((drawing) => (
                <tr
                  key={drawing.id}
                  className="border-t hover:bg-gray-50 cursor-pointer text-sm"
                  onClick={() => onSelectDrawing(drawing)}
                >
                  <td className="py-3 px-4">{drawing.name}</td>
                  <td className="py-3 px-4">{drawing.projectName}</td>
                  <td className="py-3 px-4">{drawing.fileType}</td>
                  <td className="py-3 px-4">{drawing.assignee}</td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${getStatusBadgeClass(
                        drawing.status
                      )}`}
                    >
                      {drawing.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">{drawing.lastUpdated}</td>
                  <td
                    className="py-3 px-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <button
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        onClick={() => onSelectDrawing(drawing)}
                        aria-label="View"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadDiagram(drawing.diagram, drawing.name);
                        }}
                        aria-label="Download"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        onClick={() => setDeleteId(drawing.id)}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredDrawings.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <p className="text-sm font-medium">No designs found</p>
                <p className="text-xs text-gray-500 mt-1">
                  Try adjusting your filters
                </p>
              </div>
            </div>
          )}
        </div>

        <CreateDesignModal
          show={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          formData={formData}
          setFormData={setFormData}
          projects={projects}
          teamMembers={teamMembers}
          designTypes={designTypes}
          onSubmit={handleSubmitCreate}
        />

        {deleteId && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded">
              <p>Are you sure you want to delete this design?</p>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  className="px-3 py-1 border rounded"
                  onClick={() => setDeleteId(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1 bg-red-600 text-white rounded"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
